// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: people, not friends. A Konvo instance is gated per organisation, so
// everyone in the org community is someone you can message, mention or ask
// /free about. These helpers give every picker one source of people and one
// switch to hide the inherited friend-request machinery. When the instance is
// not running in single-community mode they fall back to the friends list,
// so nothing changes for an open instance.

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import GuildMembers from '@app/features/member/state/GuildMembers';
import Relationships from '@app/features/relationship/state/Relationships';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {RelationshipTypes} from '@konvo/constants/src/UserConstants';

/** Guild id the member list was last asked for, and when. */
let requestedGuildId: string | null = null;
let requestedAt = 0;
const REQUEST_RETRY_MS = 10_000;

/** True when the instance is one organisation and every member counts as a contact. */
export function isOrgWide(): boolean {
	return RuntimeConfig.singleCommunityEnabled && RuntimeConfig.singleCommunityGuildId != null;
}

export function getOrgGuildId(): string | null {
	return isOrgWide() ? RuntimeConfig.singleCommunityGuildId : null;
}

/** Friend-request actions and pages have no place in an org-gated instance. */
export function friendActionsHidden(): boolean {
	return RuntimeConfig.directMessagesDisabled || isOrgWide();
}

interface OrgPeopleOptions {
	includeSelf?: boolean;
}

function friendUsers(): Array<User> {
	const users: Array<User> = [];
	for (const relationship of Relationships.getRelationships()) {
		if (relationship.type !== RelationshipTypes.FRIEND) continue;
		const user = Users.getUser(relationship.id);
		if (user) users.push(user);
	}
	return users;
}

/**
 * Everyone you can reach: the org community's members when org-wide (Teams
 * shadow accounts included, the system account excluded), otherwise friends.
 */
export function getOrgPeople(options: OrgPeopleOptions = {}): Array<User> {
	const guildId = getOrgGuildId();
	const currentUserId = Users.currentUser?.id ?? null;
	const source = guildId ? GuildMembers.getMembers(guildId).map((member) => member.user) : friendUsers();
	const seen = new Set<string>();
	const people: Array<User> = [];
	for (const user of source) {
		if (!user || seen.has(user.id)) continue;
		if (user.system) continue;
		if (!options.includeSelf && user.id === currentUserId) continue;
		seen.add(user.id);
		people.push(user);
	}
	return people;
}

/** Whether a user is one of "our people": an org member, or a friend when not org-wide. */
export function isOrgPerson(userId: string): boolean {
	const guildId = getOrgGuildId();
	if (guildId) return GuildMembers.getMember(guildId, userId) != null;
	return Relationships.getRelationship(userId)?.type === RelationshipTypes.FRIEND;
}

/**
 * Ask the gateway for the whole org member list so pickers are complete.
 * The request is fire-and-forget over the socket, so it is repeated while
 * the list is still empty (no socket yet at first paint, or a reconnect
 * in between), at most every ten seconds.
 */
export function ensureOrgPeopleLoaded(): void {
	const guildId = getOrgGuildId();
	if (!guildId) return;
	const loaded = GuildMembers.getMembers(guildId).length > 1;
	const now = Date.now();
	if (requestedGuildId === guildId && (loaded || now - requestedAt < REQUEST_RETRY_MS)) return;
	if (!GatewayConnection.socket) return;
	requestedGuildId = guildId;
	requestedAt = now;
	GuildMembers.requestMembersInBackground({guildIds: [guildId], query: '', limit: 0, presences: true});
}
